import { AxiosResponse } from "axios";
import config from "../../../../config/config";
import ApiClient from "../../ApiClient";
import {
    InvoiceFileResponse,
    InvoiceRequest,
    InvoiceResponse, InvoiceTempPdf, SendMailDto
} from "./types";
import {EmailLogResponse} from "../emailLog/types";

const api = new ApiClient("/Invoice", config.api.baseUrl);

/**
 * Get all invoices basic information.
 */
export const getAllInvoices = (): Promise<InvoiceResponse[]> => {
    return api
        .get(`/List`)
        .then((response: AxiosResponse<InvoiceResponse[]>) => {
            return Promise.resolve(response.data);
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};

/**
 * Get invoice basic information.
 * @param invoiceId
 */
export const getInvoiceDetails = (invoiceId: number): Promise<InvoiceResponse> => {
    return api
        .get(`/${invoiceId}`)
        .then((response: AxiosResponse<InvoiceResponse>) => {
            return Promise.resolve(response.data);
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};
/**
 * Get invoice default data.
 */
export const getInitialData = (offerID?: number|null): Promise<InvoiceResponse> => {
    let url = `/Defaults`;
    if (offerID) {
        url += `?offerID=${offerID}`;
    }
    return api
        .get(url)
        .then((response: AxiosResponse<InvoiceResponse>) => {
            return Promise.resolve(response.data);
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};

/**
 * Generate printable invoice
 * @param invoiceId
 */
export const generateInvoice = (invoiceId: number): Promise<InvoiceTempPdf> => {
    return api
        .get(`/${invoiceId}/Generate`, {
            responseType: "blob",
        })
        .then((response: AxiosResponse<Blob>) => {
            return Promise.resolve({
                data: response.data,
                id: response.headers["x-invoice-file-id"].split(",").map((id: string) => parseInt(id.trim()))
            });
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};

/**
 * Send email regarding invoice
 * @param invoiceId
 */
export const sendEmail = (invoiceId: number, data: SendMailDto): Promise<void> => {
    return api
        .post(`/${invoiceId}/SendEmail`, data)
        .then((response: AxiosResponse<Blob>) => {
            return Promise.resolve();
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};

/**
 * Get send email default data.
 */
export const getSendEmailInitialData = (invoiceId: number): Promise<SendMailDto> => {
    return api
        .get(`/${invoiceId}/SendEmailDefaults`)
        .then((response: AxiosResponse<SendMailDto>) => {
            return Promise.resolve(response.data);
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};

/**
 * Update invoice information.
 * @param invoiceID
 * @param data
 */
export const updateInvoice = (invoiceId: number, data: InvoiceRequest): Promise<InvoiceResponse> => {
    return api
        .post(`/${invoiceId}`, data)
        .then((response: AxiosResponse<InvoiceResponse>) => {
            return Promise.resolve(response.data);
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};

/**
 * Add new invoice.
 * @param token
 * @param data
 */
export const addInvoice = (data: InvoiceRequest): Promise<InvoiceResponse> => {
    return api
        .post(`/New`, data)
        .then((response: AxiosResponse<InvoiceResponse>) => {
            return Promise.resolve(response.data);
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};


/**
 * Delete invoice.
 * @param token
 * @param invoiceId
 */
export const deleteInvoice = (invoiceId: number): Promise<void> => {
    return api
        .delete(`/${invoiceId}`)
        .then((response: AxiosResponse<void>) => {
            return Promise.resolve(response.data);
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};

/**
 * Get all invoices attachments.
 * @param token
 * @param code
 */
export const getAllInvoiceAttachments = (invoiceId: number): Promise<InvoiceFileResponse[]> => {
    return api
        .get(`/${invoiceId}/Files`)
        .then((response: AxiosResponse<InvoiceFileResponse[]>) => {
            return Promise.resolve(response.data);
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};

/**
 * Delete invoice attachment.
 * @param token
 * @param code
 */
export const deleteInvoiceAttachment = (invoiceId: number, attachmentId: number): Promise<void> => {
    return api
        .delete(`/${invoiceId}/Attachment/${attachmentId}`)
        .then((response: AxiosResponse<void>) => {
            return Promise.resolve(response.data);
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};

/**
 * Get current exchange rate from CNB.
 * @param currencyCode 3-letter currency code (e.g., EUR, USD)
 */
export const getCurrentExchangeRate = (currencyCode: string): Promise<number> => {
    return api
        .get(`/ExchangeRate/${currencyCode}`)
        .then((response: AxiosResponse<number>) => {
            return Promise.resolve(response.data);
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};

/**
 * Get emails sent regarding the invoice (newest first).
 * @param invoiceId
 */
export const getInvoiceEmails = (invoiceId: number): Promise<EmailLogResponse[]> => {
    return api
        .get(`/${invoiceId}/Emails`)
        .then((response: AxiosResponse<EmailLogResponse[]>) => {
            return Promise.resolve(response.data);
        })
        .catch((error) => {
            return Promise.reject(error);
        });
};
